import { useEffect, useMemo, useState } from 'react';
import ManageShell, { type ManageMenuGroup } from '../manage-common/ManageShell';
import { getUserSession } from '../screens/login/userSession';
import { errorMessage, financeGet, ROLE_LABEL, type FinanceCtx, type FinanceRole } from './financeApi';
import FinanceDashboard from './dashboard/FinanceDashboard';
import IncomeRecords from './income/IncomeRecords';
import IncomeBulkUpload from './income/IncomeBulkUpload';
import BankMatching from './income/BankMatching';
import ExpenseRequests from './expense/ExpenseRequests';
import ExpenseRecords from './expense/ExpenseRecords';
import AnnualBudget from './budget/AnnualBudget';
import BudgetExecution from './budget/BudgetExecution';
import SettlementReport from './reports/SettlementReport';
import DonationReceipts from './receipts/DonationReceipts';
import FinanceSettingsPage from './settings/FinanceSettingsPage';
import FinanceRoles from './settings/FinanceRoles';
import './Finance.scss';

// 재정관리(/manage-finance): 재정 RN앱(com.*churchfinance)의 웹 버전.
// 권한은 서버가 판단(최종관리자 또는 재정 권한을 받은 일반관리자) - 권한이 없으면 화면에 403 안내가 뜬다.
// 로그인 이후 권한이 바뀌어도 바로 반영되도록 클라이언트 세션 값으로는 막지 않고, 콘솔을 열 때 GET /finance/me로
// 권한 등급(재정부장/재정부원/조회)을 받아 메뉴와 버튼을 맞춘다. 실제 차단은 서버가 매 요청 다시 한다.
export type FinanceMenuKey =
  | 'dashboard'
  | 'income'
  | 'incomeBulk'
  | 'bank'
  | 'expenseRequests'
  | 'expense'
  | 'budget'
  | 'execution'
  | 'settlement'
  | 'receipts'
  | 'settings'
  | 'roles';

function buildMenu(role: FinanceRole | null): ManageMenuGroup<FinanceMenuKey>[] {
  const writer = role === 'manager' || role === 'clerk';
  const groups: ManageMenuGroup<FinanceMenuKey>[] = [
    { group: '홈', items: [{ key: 'dashboard', label: '대시보드', icon: '📊' }] },
    {
      group: '수입 관리',
      items: [
        { key: 'income', label: '헌금 등록·내역', icon: '🙏' },
        ...(writer
          ? [
              { key: 'incomeBulk' as const, label: '엑셀 일괄 입력', icon: '📥' },
              { key: 'bank' as const, label: '통장 입금 매칭', icon: '🏦' },
            ]
          : []),
      ],
    },
    {
      group: '지출 관리',
      items: [
        { key: 'expenseRequests', label: '지출 결의서', icon: '🧾' },
        { key: 'expense', label: '지출 내역', icon: '💸' },
      ],
    },
    {
      group: '예산·결산',
      items: [
        { key: 'budget', label: '연간 예산 편성', icon: '🗂️' },
        { key: 'execution', label: '예산 대비 집행', icon: '📈' },
        { key: 'settlement', label: '결산 보고서', icon: '📑' },
      ],
    },
    { group: '연말정산', items: [{ key: 'receipts', label: '기부금 영수증', icon: '🎫' }] },
    {
      group: '설정',
      items: [
        { key: 'settings', label: '재정 설정', icon: '⚙️' },
        ...(role === 'manager' ? [{ key: 'roles' as const, label: '재정 권한', icon: '🔐' }] : []),
      ],
    },
  ];
  return groups;
}

export default function ManageFinanceConsole() {
  const [activeMenu, setActiveMenu] = useState<FinanceMenuKey>('dashboard');
  const session = getUserSession('finance');
  const [role, setRole] = useState<FinanceRole | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    financeGet<{ role: FinanceRole }>({ authChurch: session.authChurch, requesterId: session.id, role: 'viewer' }, '/me')
      .then((data) => setRole(data.role))
      .catch((err) => setRoleError(errorMessage(err, '재정 권한을 확인하지 못했습니다.')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.authChurch, session?.id]);

  const menuGroups = useMemo(() => buildMenu(role), [role]);
  // 하위 화면들이 ctx를 effect 의존성으로 쓰므로 값이 바뀔 때만 새 객체를 만든다.
  const authChurch = session?.authChurch ?? '';
  const requesterId = session?.id ?? 0;
  const ctx = useMemo<FinanceCtx | null>(
    () => (role ? { authChurch, requesterId, role } : null),
    [authChurch, requesterId, role],
  );

  return (
    <ManageShell<FinanceMenuKey>
      console="finance"
      menuGroups={roleError ? [] : menuGroups}
      activeMenu={activeMenu}
      onSelectMenu={setActiveMenu}
    >
      {() => {
        if (roleError) return <div className="manage-state manage-state--error">{roleError}</div>;
        if (!role || !ctx) return <div className="manage-state">권한을 확인하는 중입니다...</div>;
        const go = (key: FinanceMenuKey) => {
          window.scrollTo(0, 0);
          setActiveMenu(key);
        };
        return (
          <>
            <div className="fin-role-note">
              내 재정 권한: <b>{ROLE_LABEL[role]}</b>
              {role === 'viewer' && <span> · 조회만 가능하며 등록·수정은 할 수 없습니다.</span>}
            </div>
            {activeMenu === 'dashboard' && <FinanceDashboard ctx={ctx} onNavigate={go} />}
            {activeMenu === 'income' && <IncomeRecords ctx={ctx} />}
            {activeMenu === 'incomeBulk' && <IncomeBulkUpload ctx={ctx} onDone={() => go('income')} />}
            {activeMenu === 'bank' && <BankMatching ctx={ctx} onDone={() => go('income')} />}
            {activeMenu === 'expenseRequests' && <ExpenseRequests ctx={ctx} />}
            {activeMenu === 'expense' && <ExpenseRecords ctx={ctx} />}
            {activeMenu === 'budget' && <AnnualBudget ctx={ctx} />}
            {activeMenu === 'execution' && <BudgetExecution ctx={ctx} />}
            {activeMenu === 'settlement' && <SettlementReport ctx={ctx} />}
            {activeMenu === 'receipts' && <DonationReceipts ctx={ctx} />}
            {activeMenu === 'settings' && <FinanceSettingsPage ctx={ctx} />}
            {activeMenu === 'roles' && role === 'manager' && <FinanceRoles ctx={ctx} />}
          </>
        );
      }}
    </ManageShell>
  );
}
